<template>
  <div>
    <b-tabs v-model="activeMainTab" class="mb-2">
      <b-tab-item :label="$t('integrations.lambdaBuilder')" :value="'form'">
        <div class="box">
          <h2 class="subtitle">{{ $t('integrations.lambdaIntegration') }}</h2>

          <BFieldHint :label="$t('integrations.name')" :hintKey="$t('integrations.nameHelp')">
            <b-input ref="nameInput" v-model="form.name" :placeholder="$t('integrations.namePlaceholder')" required maxlength="64" pattern="[a-Z/_0-9]*" :validation-message="$t('integrations.nameValidationError')"/>
          </BFieldHint>

          <BFieldHint :label="$t('integrations.device')" hintKey="integrations.deviceHelp">
            <b-select v-model="form.deviceId">
              <option :value="'*'">{{ $t('integrations.anyDevice') }}</option>
              <option v-for="d in deviceList" :key="d.id" :value="d.id">{{ d.name || d.id }}</option>
            </b-select>
          </BFieldHint>

          <BFieldHint :label="$t('integrations.status')" hintKey="integrations.statusHelp">
            <b-switch v-model="form.enabled">{{ form.enabled ? ($t('integrations.enabled')) : ($t('integrations.disabled')) }}</b-switch>
          </BFieldHint>

          <BFieldHint :label="$t('global.secrets')" hintKey="integrations.secretsHelp">
            <b-taginput
              v-model="selectedSecrets"
              :data="filteredSecretOptions"
              autocomplete
              :open-on-focus="true"
              :allow-new="false"
              :placeholder="$t('secrets.selectSecretsPlaceholder')"
              type="is-primary"
              @typing="onSecretTyping"
            />
            <div class="mt-2">
              <b-button type="is-primary" @click="secretModalOpen = true">{{ $t('secrets.addNewButton') }}</b-button>
            </div>
          </BFieldHint>

          <div v-if="form.parameters && form.parameters.length" class="mt-4">
            <h2 class="subtitle">{{ $t('integrations.parameters')}}</h2>
            <div class="columns is-mobile" v-for="(param, idx) in form.parameters" :key="param.template_name || idx">
              <div class="column">
                <b-field :label="param.template_name || ('Param ' + (idx + 1))">
                  <b-input v-model="param.value" />
                </b-field>
              </div>
            </div>
          </div>

          <BFieldHint :label="$t('integrations.language')" hintKey="integrations.languageHelp">
            <b-select v-model="form.language">
              <option value="javascript">JavaScript</option>
            </b-select>
          </BFieldHint>

          <BFieldHint :label="$t('integrations.code')" hintKey="integrations.codeHelp">
            <div class="codemirror-wrapper">
              <codemirror
                v-model="form.code"
                :style="{ height: '520px', width: '100%' }"
                :indent-with-tab="true"
                :tab-size="2"
                :extensions="cmExtensions"
              />
            </div>
          </BFieldHint>
        </div>
      </b-tab-item>

      <b-tab-item :label="$t('integrations.jsonTemplate')" :value="'json'">
        <div class="box">
          <h2 class="subtitle">{{ $t('integrations.integrationJsonRepresentation') }}</h2>
          <div class="fermi-json-editor">
            <JsonEditorVue
              v-model="integrationInitial"
              :mode="jsonEditorMode"
              :stringified="false"
              :main-menu-bar="false"
              :navigation-bar="false"
              :status-bar="false"
              style="height: 520px;"
            />
          </div>
        </div>
      </b-tab-item>
    </b-tabs>

    <b-modal v-model="secretModalOpen" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" :can-cancel="['escape','x','outside']" aria-label="Add Secret" close-button-aria-label="Close" aria-modal>
      <template #default="props">
        <div class="modal-card" style="width: 600px; max-width: 95vw;">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ $t('secrets.addNew') }}</p>
            <button class="delete" aria-label="close" @click="props.close"></button>
          </header>
          <section class="modal-card-body">
            <SecretForm :rows="4" ref="secretFormInModal" @saved="handleSecretCreated" @valid="v => (canSaveSecret = v)" />
          </section>
          <footer class="modal-card-foot">
            <b-button type="is-primary" :disabled="!canSaveSecret" @click="secretFormInModal?.submit?.()">{{ $t('global.save') }}</b-button>
            <b-button @click="props.close">{{ $t('global.close') }}</b-button>
          </footer>
        </div>
      </template>
    </b-modal>

    <div>
      <b-button type="is-primary" @click="save" :loading="saving" :disabled="!isValid">{{ $t('global.save') }}</b-button>
      <b-button class="ml-2" @click="$router.back()">{{ $t('global.back') }}</b-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, computed, getCurrentInstance, watch, PropType, onMounted } from 'vue'
import { Mode } from 'vanilla-jsoneditor'

import { useStore } from 'vuex'
import JsonEditorVue from 'json-editor-vue'
import BFieldHint from '@/components/BFieldHint.vue'
import SecretForm from '@/components/SecretForm.vue'

// Vue 3 CodeMirror 6 component
import { Codemirror } from 'vue-codemirror'
import { javascript } from '@codemirror/lang-javascript'
import { oneDark } from '@codemirror/theme-one-dark'
import { vsCodeLight } from '@fsegurai/codemirror-theme-vscode-light'

export default defineComponent({
  name: 'LambdaForm',
  components: { JsonEditorVue, BFieldHint, SecretForm, codemirror: Codemirror },
  inject: ['isDarkMode'],
  props: {
    id: String,
    initial: Object as PropType<Record<string, unknown>>,
  },
  emits: ['saved'],

  computed: {
    cmExtensions() {
      return [javascript(), this.isDarkMode ? oneDark : vsCodeLight]
    }
  },

  setup(props, { emit }) {
    const store = useStore()
    const vm = getCurrentInstance()
    type DeviceSummary = { id: string; name?: string }
    type I18nProxy = { $t?: (key: string) => string }
    type IntegrationInitial = {
      name?: string
      template?: string
      deviceID?: string
      lambda?: { language?: string; code?: string }
      disabled?: boolean
      parameters?: Array<{ template_name?: string; value?: string }>
      secrets?: Array<{ template_name?: string; name?: string }>
    }

    const form = reactive({
      name: '',
      deviceId: '*',
      enabled: true,
      parameters: [] as Array<{ template_name: string; value: string }>,
      secrets: [] as Array<{ template_name?: string; name?: string }>,
      language: 'javascript',
      code: 'async function handler(ctx) {\n  return {\n    "data": ctx.data,\n    "coreid": ctx.coreid,\n    "published_at": ctx.published_at,\n    "params": ctx.params,\n    "secrets": ctx.secrets\n  };\n}',
      template: 'lambda'
    })

    // Secrets selection
    const secretModalOpen = ref(false)
    const secretFormInModal = ref<any>(null)
    const canSaveSecret = ref(false)
    const secretQuery = ref('')
    const selectedSecrets = ref<string[]>([])
    const secretOptions = computed<string[]>(() => {
      const s: any = store.state.secrets || []
      const list = Array.isArray(s) ? s : Object.values(s)
      return list.map((x: any) => String(x.name)).filter(Boolean)
    })
    const filteredSecretOptions = computed(() => {
      if (!secretQuery.value) return secretOptions.value
      const q = secretQuery.value.toUpperCase()
      return secretOptions.value.filter(n => n.toUpperCase().includes(q))
    })
    const onSecretTyping = (val: string) => { secretQuery.value = val || '' }
    const handleSecretCreated = async (newName?: string) => {
      if (newName && !selectedSecrets.value.includes(newName)) selectedSecrets.value.push(newName)
      await store.dispatch('loadModel', { model: 'secrets', force: true })
      secretModalOpen.value = false
    }

    // Validation
    const nameInput = ref<any>(null)
    type NativeField = HTMLInputElement | HTMLTextAreaElement
    const getNative = (cmp: any): NativeField | null => {
      if (!cmp) return null
      return (
        (cmp.$refs && (cmp.$refs.input as NativeField)) ||
        (cmp.$refs && (cmp.$refs.textarea as NativeField)) ||
        (cmp.$el?.querySelector?.('input, textarea') as NativeField | null) ||
        null
      )
    }
    const check = (cmp: any): boolean => {
      const el = getNative(cmp)
      return !!el && el.checkValidity()
    }
    const nameIsValid = computed(() => { void form.name; return check(nameInput.value) })
    const isValid = computed(() => true)

    // hydrate from initial when editing
    watch(() => props.initial, (val) => {
      if (!val) return
      const v = val as IntegrationInitial
      form.name = (v.name as string) || ''
      const initialDeviceId = v.deviceID as string | undefined
      form.deviceId = initialDeviceId && String(initialDeviceId).length > 0 ? String(initialDeviceId) : '*'
      if (typeof v.disabled === 'boolean') {
        form.enabled = !v.disabled
      } else {
        form.enabled = true
      }
      // parameters
      const paramsArr: any[] = v.parameters || []
      form.parameters = Array.isArray(paramsArr)
        ? paramsArr.map((p: any) => ({ template_name: String(p?.template_name || ''), value: String(p?.value || '') })).filter(p => p.template_name)
        : []
      // secrets
      const secretsArr: any[] = v.secrets || []
      form.secrets = Array.isArray(secretsArr)
        ? secretsArr.map((s: any) => ({ template_name: s?.template_name, name: s?.name }))
        : []
      selectedSecrets.value = form.secrets.map((s: any) => String(s?.name || '')).filter(Boolean)
      // lambda
      const l = v.lambda || {}
      form.language = String(l.language || 'javascript')
      form.code = String(l.code || '')
      form.template = v.template || 'lambda'
    }, { immediate: true })

    const saving = ref(false)
    const deviceList = computed<DeviceSummary[]>(() => Object.values(store.state.devices) as DeviceSummary[])

    type ToastProxy = { $buefy?: { toast?: { open?: (opts: { message: string; type: string }) => void } } }
    const openToast = (message: string, type: string) => {
      const proxy = vm?.proxy as unknown as ToastProxy
      proxy?.$buefy?.toast?.open?.({ message, type })
    }

    const save = async () => {
      saving.value = true
      try {
        const content: Record<string, unknown> = {
          name: form.name,
          lambda: { language: form.language, code: form.code },
          parameters: form.parameters?.map(p => ({ template_name: p.template_name, value: p.value })) || [],
          secrets: (form.secrets && form.secrets.length > 0)
            ? form.secrets
            : (selectedSecrets.value.map(n => ({ name: n })))
        }
        if (form.deviceId && form.deviceId !== '*') {
          content.deviceID = form.deviceId
        }
        if (!form.enabled) {
          content.disabled = true
        }
        content.template = 'lambda'

        const targetId = props.id || crypto.randomUUID()
        await store.dispatch('upsertIntegration', { id: targetId, body: content })
        const i18n = vm?.proxy as unknown as I18nProxy
        openToast((i18n?.$t && i18n.$t('integrations.toastSaved')) || 'Saved', 'is-success')
        emit('saved')
      } catch (e) {
        const i18n = vm?.proxy as unknown as I18nProxy
        openToast((i18n?.$t && i18n.$t('integrations.toastSaveFailed')) || 'Failed to save', 'is-danger')
      } finally {
        saving.value = false
      }
    }

    const jsonEditorMode: Mode = Mode.text
    const activeMainTab = ref<'form' | 'json'>('form')

    const integrationInitial = computed<IntegrationInitial>({
      get() {
        const result: IntegrationInitial = {
          name: form.name,
          lambda: { language: form.language, code: form.code },
          template: 'lambda'
        }
        if (form.deviceId && form.deviceId !== '*') {
          (result as any).deviceID = form.deviceId
        }
        if (!form.enabled) {
          result.disabled = true
        }
        if (form.parameters && form.parameters.length > 0) {
          (result as any).parameters = form.parameters.map(p => ({ template_name: p.template_name, value: p.value }))
        }
        if (form.secrets && form.secrets.length > 0) {
          (result as any).secrets = form.secrets
        } else if (selectedSecrets.value.length > 0) {
          (result as any).secrets = selectedSecrets.value.map(n => ({ name: n }))
        }
        return result
      },
      set(val) {
        if (!val) return
        const v = val as IntegrationInitial
        form.name = (v.name as string) || ''
        if (v.deviceID) {
          form.deviceId = String(v.deviceID)
        } else {
          form.deviceId = '*'
        }
        if (typeof v.disabled === 'boolean') {
          form.enabled = !v.disabled
        } else {
          form.enabled = true
        }
        const l = v.lambda || {}
        form.language = String(l.language || 'javascript')
        form.code = String(l.code || '')
        const paramsArr: any[] = v.parameters || []
        form.parameters = Array.isArray(paramsArr)
          ? paramsArr.map((p: any) => ({ template_name: String(p?.template_name || ''), value: String(p?.value || '') })).filter(p => p.template_name)
          : []
        const secretsArr: any[] = v.secrets || []
        form.secrets = Array.isArray(secretsArr)
          ? secretsArr.map((s: any) => ({ template_name: s?.template_name, name: s?.name }))
          : []
        selectedSecrets.value = Array.isArray(secretsArr) ? secretsArr.map((s: any) => String(s?.name || '')).filter(Boolean) : []
        form.template = v.template || 'lambda'
      }
    })

    onMounted(async () => {
      await store.dispatch('loadModel', { model: 'secrets' })
    })

    // Slot helpers for parameters/secrets editing from parent if needed
    const getParam = (name: string): string => {
      const found = Array.isArray((form as any).parameters)
        ? (form as any).parameters.find((p: any) => p?.template_name === name)
        : null
      return String(found?.value ?? '')
    }
    const setParam = (name: string, value: string) => {
      if (!Array.isArray((form as any).parameters)) (form as any).parameters = []
      const idx = (form as any).parameters.findIndex((p: any) => p?.template_name === name)
      if (idx >= 0) (form as any).parameters[idx].value = value
      else (form as any).parameters.push({ template_name: name, value })
    }
    const getSecret = (templateName: string): string => {
      const found = Array.isArray((form as any).secrets)
        ? (form as any).secrets.find((s: any) => s?.template_name === templateName)
        : null
      return String(found?.name || '')
    }
    const setSecret = (templateName: string, secretName: string) => {
      if (!Array.isArray((form as any).secrets)) (form as any).secrets = []
      const idx = (form as any).secrets.findIndex((s: any) => s?.template_name === templateName)
      if (idx >= 0) {
        (form as any).secrets[idx].name = secretName
        if ((form as any).secrets[idx].value) delete (form as any).secrets[idx].value
      } else {
        (form as any).secrets.push({ template_name: templateName, name: secretName })
      }
      if (secretName && selectedSecrets?.value && !selectedSecrets.value.includes(secretName)) {
        selectedSecrets.value.push(secretName)
      }
    }

    return {
      form,
      deviceList,
      save,
      saving,
      jsonEditorMode,
      activeMainTab,
      integrationInitial,
      selectedSecrets,
      secretModalOpen,
      filteredSecretOptions,
      onSecretTyping,
      handleSecretCreated,
      secretFormInModal,
      canSaveSecret,
      getSecret,
      setSecret,
      getParam,
      setParam,
      isValid,
      nameInput
    }
  }
})
</script>
